/*@flow*/
import assert from './assert';
import isTypeName from './isTypeName';
import forbidNewOperator from './forbidNewOperator';
import isNumber from './isNumber';
import isString from './isString';
import isObject from './isObject';
import hasOwn from './hasOwn';
import type { TypeT } from './types';
import withOptions from './withOptions';

type StringMapT = { [key: string]: string };
type EnumType = TypeT<{ identity: true, map: StringMapT }>;

function getDefaultName(map): string {
  return Object.keys(map)
    .map(function(k) {
      return assert.stringify(k);
    })
    .join(' | ');
}

function enums(map: StringMapT, name?: string): EnumType {
  if (process.env.NODE_ENV !== 'production') {
    assert(isObject(map), function() {
      return (
        'Invalid argument map ' +
        assert.stringify(map) +
        ' supplied to enums(map, [name]) combinator (expected a dictionary of String -> String | Number)'
      );
    });
    assert(isTypeName(name), function() {
      return (
        'Invalid argument name ' +
        assert.stringify(name) +
        ' supplied to enums(map, [name]) combinator (expected a string)'
      );
    });
  }

  let displayName = name || getDefaultName(map);

  function Enums(value: any, path?: string[]) {
    let path1 = path || [displayName];

    if (process.env.NODE_ENV !== 'production') {
      forbidNewOperator(this, Enums);

      assert(Enums.is(value), function() {
        return (
          'Invalid value ' +
          assert.stringify(value) +
          ' supplied to ' +
          path1.join('/') +
          ' (expected one of ' +
          assert.stringify(Object.keys(map)) +
          ')'
        );
      });
    }

    return value;
  }

  Enums.meta = {
    kind: 'enums',
    map,
    name,
    identity: true,
  };

  Enums.displayName = displayName;

  Enums.is = function(x) {
    return (isString(x) || isNumber(x)) && hasOwn.call(map, x);
  };

  withOptions(Enums);

  return Enums;
}

enums.of = (keys, name): EnumType => {
  let keys2 = isString(keys) ? keys.split(' ') : keys;
  let value = {};
  keys2.forEach(k => {
    value[k] = k;
  });
  return enums(value, name);
};

enums.getDefaultName = getDefaultName;
export default enums;
